import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Column, Card } from '../types';
import TaskCard from './TaskCard';

interface Props {
  column: Column;
  cards: Card[];
  onCardClick: (card: Card) => void;
  onAddCard: (columnId: string) => void;
  isAdmin?: boolean;
  canDragCard?: (card: Card) => boolean;
}

export default function KanbanColumn({ column, cards, onCardClick, onAddCard, isAdmin = true, canDragCard }: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: 300,
        minWidth: 300,
        maxHeight: '100%',
        background: isOver ? '#EFF6FF' : '#F1F5F9',
        borderRadius: 16,
        border: isOver ? '2px dashed #2563EB' : '2px solid transparent',
        transition: 'border-color 0.15s, background 0.15s',
        overflow: 'hidden',
      }}
    >
      {/* Column Header */}
      <div
        style={{
          padding: '14px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: column.color,
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', letterSpacing: '0.01em' }}>
            {column.name.toUpperCase()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              background: 'white',
              color: '#64748B',
              fontSize: 12,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 100,
              border: '1px solid #E2E8F0',
            }}
          >
            {cards.length}
          </span>
          <button
            onClick={() => onAddCard(column.id)}
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              border: 'none',
              background: 'white',
              cursor: 'pointer',
              display: isAdmin ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748B',
              fontSize: 18,
              lineHeight: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.background = '#2563EB';
              (e.target as HTMLElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'white';
              (e.target as HTMLElement).style.color = '#64748B';
            }}
            title={`Add card to ${column.name}`}
          >
            +
          </button>
        </div>
      </div>

      {/* Cards */}
      <div
        ref={setNodeRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '4px 10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          minHeight: 80,
        }}
      >
        <SortableContext
          items={cards.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map((card) => (
            <TaskCard key={card.id} card={card} onClick={() => onCardClick(card)} canDrag={canDragCard ? canDragCard(card) : isAdmin} />
          ))}
        </SortableContext>

        {cards.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '24px 16px',
              color: '#94A3B8',
              fontSize: 13,
              border: '2px dashed #E2E8F0',
              borderRadius: 12,
              cursor: isAdmin ? 'pointer' : 'default',
            }}
            onClick={isAdmin ? () => onAddCard(column.id) : undefined}
          >
            {isAdmin ? 'Drop cards here' : 'No cards'}
          </div>
        )}
      </div>
    </div>
  );
}
